import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Loader2, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { API, isoDate, fmtDayLong } from './_helpers';

function NewLeaveModal({ onClose, onSaved }) {
  const [type, setType] = useState('annual');
  const [startDate, setStartDate] = useState(isoDate(new Date()));
  const [endDate, setEndDate] = useState(isoDate(new Date()));
  const [hpd, setHpd] = useState(8);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/roster/leave`,
        { type, start_date: startDate, end_date: endDate, hours_per_day: parseFloat(hpd), reason: reason || null },
        { withCredentials: true });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save'); setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-3">Request leave</h3>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-muted-foreground">Type
            <select value={type} onChange={e => setType(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="leave-new-type">
              <option value="annual">Annual</option>
              <option value="sick">Sick</option>
              <option value="personal">Personal / carer&apos;s</option>
              <option value="unpaid">Unpaid</option>
              <option value="rdo">RDO</option>
              <option value="parental">Parental</option>
              <option value="study">Study</option>
              <option value="other">Other</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-semibold text-muted-foreground">From
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1" />
            </label>
            <label className="text-xs font-semibold text-muted-foreground">To
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1" />
            </label>
          </div>
          <label className="text-xs font-semibold text-muted-foreground">Hours per day
            <Input type="number" min="0.5" max="24" step="0.5" value={hpd} onChange={e => setHpd(e.target.value)} className="mt-1" />
          </label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Reason (optional)" />
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
          <button onClick={submit} disabled={busy} className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50"
                  data-testid="leave-new-save">
            {busy ? 'Saving…' : 'Submit request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeavePanel({ isManager }) {
  const [requests, setRequests] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/roster/leave`, { withCredentials: true });
      setRequests(r.data.requests);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (lid, decision) => {
    const notes = decision === 'declined' ? window.prompt('Reason for declining? (optional)') : '';
    try {
      await axios.patch(`${API}/roster/leave/${lid}/decide`, { decision, notes }, { withCredentials: true });
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Decide failed'); }
  };
  const cancel = async (lid) => {
    if (!window.confirm('Cancel this leave request?')) return;
    await axios.delete(`${API}/roster/leave/${lid}`, { withCredentials: true });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold" style={{ color: '#0d2644' }}>
          {isManager ? 'All leave requests' : 'My leave requests'}
        </h2>
        <button onClick={() => setShowNew(true)} data-testid="leave-new"
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#0d2644] text-white">
          <Plus className="w-4 h-4" /> Request leave
        </button>
      </div>
      {loading && <div className="text-center text-muted-foreground py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && !requests.length && (
        <div className="text-center text-muted-foreground py-8 border rounded-xl bg-card">No leave requests yet.</div>
      )}
      <div className="space-y-2">
        {requests.map(r => (
          <div key={r.leave_id} className="border rounded-lg bg-card p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`leave-${r.leave_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{
                  background: r.status==='approved' ? '#dcfce7' : r.status==='declined' ? '#fee2e2' :
                              r.status==='cancelled' ? '#f1f5f9' : '#fef3c7',
                  color: r.status==='approved' ? '#15803d' : r.status==='declined' ? '#b91c1c' :
                         r.status==='cancelled' ? '#475569' : '#d97706',
                }}>{r.status}</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{r.type}</span>
              </div>
              <div className="mt-1 text-sm font-semibold" style={{ color: '#0d2644' }}>
                {fmtDayLong(r.start_date)} → {fmtDayLong(r.end_date)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {r.user_name} · {r.days} day{r.days===1?'':'s'} · {r.total_hours}h
              </div>
              {r.reason && <div className="text-xs mt-1 italic">&ldquo;{r.reason}&rdquo;</div>}
              {r.decision_notes && <div className="text-xs mt-1 text-muted-foreground"><strong>Decision:</strong> {r.decision_notes}</div>}
            </div>
            <div className="flex items-center gap-1.5">
              {isManager && r.status === 'pending' && (
                <>
                  <button onClick={() => decide(r.leave_id, 'approved')} className="text-xs font-semibold px-3 py-1.5 rounded bg-green-600 text-white"
                          data-testid={`leave-approve-${r.leave_id}`}>Approve</button>
                  <button onClick={() => decide(r.leave_id, 'declined')} className="text-xs font-semibold px-3 py-1.5 rounded border border-red-600 text-red-600"
                          data-testid={`leave-decline-${r.leave_id}`}>Decline</button>
                </>
              )}
              {r.status === 'pending' && (
                <button onClick={() => cancel(r.leave_id)} className="text-xs px-2 py-1.5 rounded border" title="Cancel request">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {showNew && <NewLeaveModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
